import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Heart, LayoutGrid, LifeBuoy, LogOut, Package, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { focusRing } from '../styles/ui';

// Only destinations this app actually has. Account is a section of /account
// rather than its own route, so it is an anchor into that page; `section`
// marks it so an in-page click scrolls instead of re-navigating. The wallet
// and delivery-address sections remain on the account page itself — they are
// simply no longer listed here.
const LINKS = [
  { to: '/account', label: 'Dashboard', icon: LayoutGrid },
  { to: '/orders', label: 'My Orders', icon: Package },
  { to: '/favourites', label: 'Favourites', icon: Heart },
  { to: '/account#profile', section: 'profile', label: 'Account', icon: Settings },
  { to: '/support', label: 'Help & Support', icon: LifeBuoy },
];

// Matches the fixed header height used across the site (config/navigation.js).
const HEADER_OFFSET = 96;

const initialsFor = (name, email) => {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 1).toUpperCase();
};

const AccountSidebar = () => {
  const { customer, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Already on /account? Scroll to the section rather than re-rendering the
  // route. Anywhere else, the link navigates and the page handles the hash.
  const handleSectionClick = (event, section) => {
    if (!section || pathname !== '/account') return;
    const target = document.getElementById(section);
    if (!target) return;
    event.preventDefault();
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET,
      behavior: 'smooth',
    });
  };

  return (
    <aside className="bg-white rounded-2xl border border-[#E6EEE6] shadow-[0_10px_30px_-16px_rgba(15,51,24,0.3)] p-2 lg:px-4 lg:pt-5 lg:pb-4">
      <h2 className="hidden lg:block serif text-[20px] font-bold text-emerald-950 mb-3 lg:mb-4 lg:px-1">My Account</h2>

      {/* Identity comes from the authenticated session only — never a literal. */}
      {/* Phones and tablets get only the section scroller; the identity block
          is for the desktop sidebar. */}
      <div className="hidden lg:flex items-center gap-3 pb-3 lg:pb-4 lg:px-1">
        <span className="w-[54px] h-[54px] rounded-full bg-[#E4F3E5] text-emerald-800 flex items-center justify-center text-[20px] font-bold shrink-0">
          {initialsFor(customer?.name, customer?.email)}
        </span>
        <span className="min-w-0">
          <span className="block text-[14px] font-bold text-emerald-950 leading-tight truncate">
            {customer?.name || 'Your account'}
          </span>
          {customer?.email && (
            <span className="block text-[12px] text-ink-secondary leading-tight truncate mt-[3px]">
              {customer.email}
            </span>
          )}
          <Link
            to="/account#profile"
            onClick={(event) => handleSectionClick(event, 'profile')}
            className={`mt-[6px] inline-flex items-center gap-1 text-[12px] font-bold text-[#1E7A3C] ${focusRing}`}
          >
            Edit Profile
            <ArrowRight size={12} aria-hidden="true" />
          </Link>
        </span>
      </div>

      <div className="hidden lg:block border-t border-[#EEF3EE] -mx-4 mb-3" />

      <nav aria-label="Account">
        <ul className="flex lg:block gap-2 lg:gap-0 lg:space-y-1 overflow-x-auto no-scrollbar lg:overflow-visible">
          {LINKS.map(({ to, label, icon: Icon, section }) => {
            // Section anchors never take the active state — Dashboard owns it
            // for /account, My Orders for /orders.
            const base = to.split('#')[0];
            const active = !section && (pathname === base || pathname.startsWith(`${base}/`));
            return (
              <li key={to} className="shrink-0 lg:shrink">
                <Link
                  to={to}
                  onClick={(event) => handleSectionClick(event, section)}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 rounded-xl px-4 h-[50px] text-[15px] whitespace-nowrap transition-colors ${focusRing} ${
                    active
                      ? 'bg-[#EAF6E6] text-emerald-900 font-semibold'
                      : 'text-[#374151] hover:bg-emerald-50/60 hover:text-emerald-800'
                  }`}
                >
                  <Icon size={19} strokeWidth={1.8} className={active ? 'text-emerald-700' : 'text-[#4B5563]'} aria-hidden="true" />
                  {label}
                </Link>
              </li>
            );
          })}
          <li className="shrink-0 lg:shrink">
            <button
              type="button"
              onClick={handleLogout}
              className={`w-full flex items-center gap-3 rounded-xl px-4 h-[50px] text-[15px] whitespace-nowrap text-[#374151] hover:bg-emerald-50/60 hover:text-emerald-800 transition-colors ${focusRing}`}
            >
              <LogOut size={19} strokeWidth={1.8} className="text-[#4B5563]" aria-hidden="true" />
              Logout
            </button>
          </li>
        </ul>
      </nav>

      {/* Promotional card: brand copy plus the design's own artwork, pointing
          at the real catalogue route. No offer, discount or claim is asserted. */}
      <Link
        to="/categories"
        className={`group relative mt-4 hidden lg:block overflow-hidden rounded-2xl bg-[linear-gradient(135deg,#F1FAEF_0%,#E4F3E5_100%)] h-[278px] ${focusRing}`}
      >
        <img
          src="/pages/orders-promo.webp"
          alt=""
          aria-hidden="true"
          loading="lazy"
          width="97"
          height="278"
          className="pointer-events-none absolute right-0 top-0 h-full w-auto"
        />
        <span className="relative block p-4 pt-[76px] max-w-[140px]">
          <span className="serif block text-[19px] font-bold text-emerald-950 leading-[1.1]">
            Freshness
            <br />
            Delivered Always
          </span>
          <span className="block text-[12.5px] text-[#4B5563] mt-2 leading-[1.35]">
            Quality food for a healthier tomorrow.
          </span>
          <span className="mt-4 inline-flex items-center gap-1.5 h-[38px] px-5 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[13px] font-bold shadow-[0_6px_14px_-6px_rgba(46,139,71,0.6)]">
            Shop Now
            <ArrowRight size={13} aria-hidden="true" />
          </span>
        </span>
      </Link>
    </aside>
  );
};

export default AccountSidebar;
